import { HttpParams } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { map, Observable } from 'rxjs';

import { CommentsService, htmlToPlainText } from '../../../../../core/services/comments.service';
import { HttpClientService } from '../../../../../core/services/http-client.service';
import { LocalStorageService } from '../../../../../core/services/local-storage.service';
import type { CaseInvestigationClient, TableDataResponse as CaseTableDataResponse } from '../../../../../core/tokens/case-investigation-client.token';
import type { EmployeeVarianceManagerClient, IncidentsQueryContext } from '../../../../../core/tokens/employee-variance-manager.token';
import type { NewCommentPayload } from '../../../../../shared/components/comments/comments.component';
import type { Comment } from '../../../../../shared/components/comments/models/comments-service.interface';
import { TableQuery } from '../../../../../shared/components/generic-table/models/table-query.model';
import { EMPLOYEE_VARIANCE_MANAGEMENT, INCIDENTS } from '../../../../../shared/constants/api-routes.constants';
import { LocalStorageKeys } from '../../../../../shared/constants/local-storage-keys';
import type { CaseInvestigationStatusLogDto } from '../../../case-investigation/models/case-investigation-status-log.model';
import type {
  CaseInvestigation,
  CaseInvestigationHeader,
  CaseInvestigationIncidentRow,
  CaseStatus as FrontendCaseStatus,
  CreateCaseInvestigation,
  IncidentStatus,
  IncidentType,
} from '../../../case-investigation/models/case-investigation.models';
import { ATTACHMENT_KINDS } from '../../../case-investigation/constants/attachment-builders';
import { TableDataResponse } from '../../../services/variance-tracker-mock.service';
import { IncidentDto } from '../models/incident-dto.model';
import { EmployeeVarianceManagementRow } from '../models/employee-variance-management-row.model';
import { mapIncidentDtoToRow } from '../utils/incident-mapper.utils';

interface CaseInvestigationApiDto {
  readonly id: number;
  readonly guid: string;
  readonly userId: number;
  readonly dateOfEvent: string;
  readonly auditorId: number;
  /** API may serialize CaseStatus as string (e.g. "Open") or number (enum value). */
  readonly status: string | number;
  readonly createdAt: string;
  readonly employeeName: string;
  readonly auditorName: string;
  readonly userStatus: string;
  readonly shiftDescription: string;
  readonly jobRole: string;
  readonly incidents: readonly CaseIncidentApiDto[];
}

interface CaseIncidentApiDto {
  readonly id: number;
  readonly varianceStatus: string;
  readonly incidentNumber: number;
  readonly medicationName: string;
  readonly varAmount: number;
  readonly varUOM: string;
  readonly patientName: string;
  readonly patientId: string;
  readonly locationName: string;
  readonly locationId: string;
  readonly shiftName: string;
  readonly shiftId: string;
  readonly dateOfIncident: string;
  readonly hourOfIncident: string;
  readonly hyperlink: string;
  readonly hyperlinkDisplayText: string;
  readonly auditorName: string;
  readonly incidentType: string;
  readonly attachments: string[];
  readonly guid: string;
  readonly normalizedMedGuid: string;
}

@Injectable({ providedIn: 'root' })
export class EmployeeVarianceManagerService implements EmployeeVarianceManagerClient, CaseInvestigationClient {
  private readonly http = inject(HttpClientService);
  private readonly commentsService = inject(CommentsService);
  private readonly localStorageService = inject(LocalStorageService);

  readonly options = signal<{ label: string; value: string }[]>([
    { label: 'Unassigned', value: 'Unassigned' },
    { label: 'Requires Review', value: 'Requires Review' },
    { label: 'Monitor', value: 'Monitor' },
    { label: 'Under Invest', value: 'Under Invest' },
    { label: 'Rev In Progress', value: 'Rev In Progress' },
    { label: 'Referral', value: 'Referral' },
    { label: 'Suspended', value: 'Suspended' },
    { label: 'Terminated', value: 'Terminated' },
    { label: 'Best Practice Coach', value: 'Best Practice Coach' },
    { label: 'Professional Support', value: 'Professional Support' },
    { label: 'Practice intervention', value: 'Practice intervention' },
    { label: 'UDS Results Pending', value: 'UDS Results Pending' },
  ]);

  getVarianceManagementRows(
    query: TableQuery,
    context: IncidentsQueryContext
  ): Observable<TableDataResponse<EmployeeVarianceManagementRow>> {
    const params = new HttpParams()
      .set('userId', context.userId)
      .set('organizationId', context.organizationId);

    return this.http
      .get<IncidentDto[]>(INCIDENTS.BASE, { params })
      .pipe(
        map((incidents) => {
          const allRows = incidents.map(mapIncidentDtoToRow);
          return this.applyTableQuery(allRows, query);
        })
      );
  }

  getHeader(caseId: string): Observable<CaseInvestigationHeader> {
    const organizationId = this.localStorageService.getItem(LocalStorageKeys.OrganizationId) ?? '';
    const params = new HttpParams().set('organizationId', organizationId);

    return this.http
      .get<CaseInvestigationApiDto>(EMPLOYEE_VARIANCE_MANAGEMENT.GET_BY_ID(Number(caseId)), { params })
      .pipe(
        map((dto) => {
          const nameParts = dto.employeeName.trim().split(' ');
          const employeeFirstName = nameParts[0] ?? '';
          const employeeLastName = nameParts.slice(1).join(' ') || employeeFirstName;

          return {
            employeeFirstName,
            employeeLastName,
            caseNumber: `Case-${dto.id}`,
            employeeId: String(dto.userId),
            employeeStatus: dto.userStatus,
            role: dto.jobRole,
            riskScore: parseFloat((Math.random() * 10).toFixed(2)),
            caseStatus: mapApiStatusToFrontend(dto.status),
          } satisfies CaseInvestigationHeader;
        })
      );
  }

  getIncidentRows(
    caseId: string,
    query: TableQuery
  ): Observable<CaseTableDataResponse<CaseInvestigationIncidentRow>> {
    const organizationId = this.localStorageService.getItem(LocalStorageKeys.OrganizationId) ?? '';
    const params = new HttpParams().set('organizationId', organizationId);

    return this.http
      .get<CaseInvestigationApiDto>(EMPLOYEE_VARIANCE_MANAGEMENT.GET_BY_ID(Number(caseId)), { params })
      .pipe(
        map((dto) => {
          const incidents = dto?.incidents ?? [];
          const allRows: CaseInvestigationIncidentRow[] = incidents.map((incident, i) => ({
            id: incident.guid,
            incidentStatus: mapVarianceStatus(incident.varianceStatus),
            incident: mapIncidentType(incident.incidentType),
            medication: incident.medicationName,
            date: incident.dateOfIncident,
            time: incident.hourOfIncident,
            auditor: incident.auditorName,
            clinicalUnit: incident.locationName,
            shift: incident.shiftName,
            attachment: ATTACHMENT_KINDS[i % ATTACHMENT_KINDS.length],
          }));

          return this.applyIncidentTableQuery(allRows, query);
        })
      );
  }

  createCaseInvestigation(caseInvestigation: CreateCaseInvestigation): Observable<CaseInvestigation> {
    return this.http.post<CaseInvestigation>(EMPLOYEE_VARIANCE_MANAGEMENT.CREATE_CASE_INVESTIGATION(), caseInvestigation);
  }

  createCaseInvestigations(caseInvestigations: CreateCaseInvestigation[]): Observable<CaseInvestigation[]> {
    return this.http.post<CaseInvestigation[]>(EMPLOYEE_VARIANCE_MANAGEMENT.CREATE_CASE_INVESTIGATIONS(), caseInvestigations);
  }

  updateCaseStatus(caseId: string, status: string, organizationId: string): Observable<void> {
    const apiStatus = mapCaseStatusToApi(status);
    const body = { status: apiStatus, organizationId: Number(organizationId) };
    return this.http.put(EMPLOYEE_VARIANCE_MANAGEMENT.UPDATE(Number(caseId)), body).pipe(map(() => undefined));
  }

  getStatusLogs(caseId: number, organizationId: number): Observable<readonly CaseInvestigationStatusLogDto[]> {
    const params = new HttpParams().set('organizationId', String(organizationId));
    return this.http.get<CaseInvestigationStatusLogDto[]>(
      EMPLOYEE_VARIANCE_MANAGEMENT.STATUS_LOGS(caseId),
      { params }
    );
  }

  getComments(caseId: string): Observable<readonly Comment[]> {
    return this.commentsService.getThread({ caseId: Number(caseId) });
  }

  addComment(caseId: string, payload: NewCommentPayload, authorUserId: string): Observable<void> {
    return this.commentsService.addComment({
      contextType: 'case_investigation',
      target: { caseId: Number(caseId) },
      authorUserId,
      content: payload.content ?? '',
      contentPlainText: htmlToPlainText(payload.content ?? ''),
      parentCommentId: null,
    });
  }

  private applyTableQuery(
    allRows: EmployeeVarianceManagementRow[],
    query: TableQuery
  ): TableDataResponse<EmployeeVarianceManagementRow> {
    let result = [...allRows];

    if (query.search) {
      const term = query.search.trim().toLowerCase();
      result = result.filter(
        (r) =>
          r.varianceStatus.toLowerCase().includes(term) ||
          String(r.Medication).toLowerCase().includes(term) ||
          String(r.Patient).toLowerCase().includes(term) ||
          String(r['Patient ID']).toLowerCase().includes(term) ||
          String(r.Date).toLowerCase().includes(term) ||
          String(r.Time).toLowerCase().includes(term) ||
          String(r['Clinical Unit']).toLowerCase().includes(term) ||
          String(r.Shift).toLowerCase().includes(term)
      );
    }

    if (query.filters && Object.keys(query.filters).length > 0) {
      result = result.filter((row) =>
        Object.entries(query.filters!).every(([field, filterValue]) => {
          const cell = row[field];
          const cellStr = cell != null ? String(cell).toLowerCase() : '';
          const term = (filterValue ?? '').trim().toLowerCase();
          return !term || cellStr.includes(term);
        })
      );
    }

    if (query.sortField) {
      const order: 1 | -1 = query.sortOrder === -1 ? -1 : 1;
      result = result.sort((a, b) => {
        const av = a[query.sortField!];
        const bv = b[query.sortField!];
        if (av == null && bv == null) return 0;
        if (av == null) return -1 * order;
        if (bv == null) return 1 * order;
        if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * order;
        return String(av).localeCompare(String(bv)) * order;
      });
    }

    const page = Math.max(0, query.page ?? 0);
    const rows = Math.max(1, query.rows ?? 10);
    const start = page * rows;
    const pageData = result.slice(start, start + rows);

    return { data: pageData, totalRecords: result.length };
  }

  private applyIncidentTableQuery(
    allRows: CaseInvestigationIncidentRow[],
    query: TableQuery
  ): CaseTableDataResponse<CaseInvestigationIncidentRow> {
    let result = [...allRows];

    if (query.search) {
      const term = query.search.trim().toLowerCase();
      result = result.filter((r) => {
        const haystack = [r.incidentStatus, r.incident, r.medication, r.date, r.time, r.auditor, r.clinicalUnit, r.shift]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return haystack.includes(term);
      });
    }

    if (query.sortField) {
      const order: 1 | -1 = query.sortOrder === -1 ? -1 : 1;
      result = result.sort((a, b) => {
        const av = a[query.sortField!];
        const bv = b[query.sortField!];
        if (av == null && bv == null) return 0;
        if (av == null) return -1 * order;
        if (bv == null) return 1 * order;
        if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * order;
        return String(av).localeCompare(String(bv)) * order;
      });
    }

    const page = Math.max(0, query.page ?? 0);
    const rows = Math.max(1, query.rows ?? 10);
    const start = page * rows;

    return { data: result.slice(start, start + rows), totalRecords: result.length };
  }
}

function mapVarianceStatus(status: string): IncidentStatus {
  const normalized = status.replace(/\s+/g, '').toLowerCase();
  switch (normalized) {
    case 'open': return 'Open';
    case 'closed': return 'Closed';
    case 'unresolved': return 'Unresolved';
    case 'pendingreponse':
    case 'pendingresponse': return 'Pending Response';
    default: return 'Under Investigation';
  }
}

function mapIncidentType(type: string): IncidentType {
  switch (type.toLowerCase()) {
    case 'alibi_escalation': return 'Alibi Escalation';
    case 'variance_escalated': return 'Variance Escalated';
    case 'rx_intercept': return 'RxIntercept';
    case 'ai_video_flag': return 'AI Video Flag';
    default: return 'Variance Escalated';
  }
}

/** Maps frontend case status label to API enum name (C# CaseStatus). */
function mapCaseStatusToApi(status: string): string {
  const map: Record<string, string> = {
    'Pending Response': 'PendingResponse',
    'Escalated to Administration': 'EscalatedToAdministration',
    'Under Investigation': 'ActiveInvestigation',
    New: 'New',
    Resolved: 'Resolved',
    Closed: 'Closed',
    Unresolved: 'Unresolved',
    Monitor: 'Monitor',
    Archived: 'Archived',
    Reviewed: 'ActiveInvestigation',
  };
  return map[status] ?? status.replace(/\s+/g, '');
}

/** C# CaseStatus enum order: Archived=0, ActiveInvestigation=1, Closed=2, EscalatedToAdministration=3, Monitor=4, New=5, PendingResponse=6, Resolved=7, Unresolved=8 */
const API_STATUS_BY_INDEX: FrontendCaseStatus[] = [
  'Archived',
  'Under Investigation', // ActiveInvestigation
  'Closed',
  'Escalated to Administration',
  'Monitor',
  'New',
  'Pending Response',
  'Resolved',
  'Unresolved',
];

/** Maps API status (string or number) to frontend CaseStatus for the dropdown. Supports PascalCase, snake_case, and numeric enum. */
function mapApiStatusToFrontend(apiStatus: string | number): FrontendCaseStatus | undefined {
  if (typeof apiStatus === 'number') {
    return API_STATUS_BY_INDEX[apiStatus];
  }
  const normalized = (apiStatus ?? '').trim();
  if (!normalized) return undefined;
  const key = normalized.replace(/\s+/g, '').replace(/_/g, '').toLowerCase();
  const map: Record<string, FrontendCaseStatus> = {
    new: 'New',
    open: 'New',
    pendingresponse: 'Pending Response',
    resolved: 'Resolved',
    closed: 'Closed',
    unresolved: 'Unresolved',
    monitor: 'Monitor',
    archived: 'Archived',
    escalatedtoadministration: 'Escalated to Administration',
    activeinvestigation: 'Under Investigation',
  };
  return map[key];
}
